import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter, fromEvent, interval, merge } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Topbar } from '../topbar/topbar';
import { Sidebar } from '../sidebar/sidebar';
import { TabBar } from '../tab-bar/tab-bar';
import { TimeseriesPopout } from '../sidebar/timeseries-popout/timeseries-popout';
import { ManagedDataPopout } from '../sidebar/managed-data-popout/managed-data-popout';
import { CustomViewsPopout } from '../sidebar/custom-views-popout/custom-views-popout';
import { CommandPalette } from '../command-palette/command-palette';
import { RolePreviewDialog } from '../role-preview-dialog/role-preview-dialog';
import { isAnessaEmail } from '../command-palette/command-palette-dev-commands';
import { COMMAND_PALETTE_NAMESPACES } from '../command-palette/command-palette-pages';
import { tabTitleFor } from '../command-palette/tab-title';
import { DashboardService } from '../../features/dashboard/services/dashboard/dashboard.service';
import { MyAuthService } from '../../auth/auth.service';
import { PermissionService } from '../../shared/services/permissions/permission.service';
import { I18nService } from '../../shared/i18n/i18n.service';
import { TabViewService } from '../../shared/tab-view/tab-view.service';
import { DevNotesService } from '../../shared/dev-notes/dev-notes.service';
import { ViewportService } from '../../shared/services/viewport/viewport.service';
import { MonthLockService } from '../../shared/month-lock/month-lock.service';
import { MonthUnlockBanner } from '../../shared/month-lock/month-unlock-banner/month-unlock-banner';
import { RedSchedulerClaimBanner } from '../../shared/red-scheduler-claim/red-scheduler-claim-banner/red-scheduler-claim-banner';

@Component({
  selector: 'app-layout',
  imports: [
    RouterModule,
    Topbar,
    Sidebar,
    TabBar,
    TimeseriesPopout,
    ManagedDataPopout,
    CustomViewsPopout,
    MatIconModule,
    CommonModule,
    MonthUnlockBanner,
    RedSchedulerClaimBanner,
  ],
  templateUrl: './layout.html',
  styleUrl: './layout.scss',
})
export class Layout implements OnInit {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly authService = inject(MyAuthService);
  private readonly devNotes = inject(DevNotesService);
  // Exposed for the role-preview banner (see setPreviewRole / the ~changerole dev command).
  protected readonly permissionService = inject(PermissionService);
  // Exposed for the tab bar's `@if` gate (see the ~tabs dev command).
  protected readonly tabViewService = inject(TabViewService);
  private readonly i18n = inject(I18nService);
  private readonly dashboardService = inject(DashboardService);
  protected readonly viewport = inject(ViewportService);

  /** Desktop icon-only menu (☰), remembered per browser (ADEV-1215, CVES-18). */
  protected sidebarCollapsed = readSidebarCollapsed();
  /** Compact viewports only: whether the overlay sidebar drawer is shown. */
  protected readonly drawerOpen = signal(false);
  protected timeseriesPopoutOpen = false;
  protected managedDataPopoutOpen = false;
  protected customViewsPopoutOpen = false;

  readonly mainRef = viewChild.required('mainRef', { read: ElementRef });

  overlayStyle = {};
  get isBlurred(): boolean {
    return (
      this.timeseriesPopoutOpen ||
      this.managedDataPopoutOpen ||
      this.customViewsPopoutOpen
    );
  }

  protected exitRolePreview(): void {
    this.permissionService.clearPreviewRole();
  }

  // Keeps the escape-hatch unlock state current (see the ~unlock / ~relock dev commands).
  private readonly monthLock = inject(MonthLockService);
  private commandPaletteRef: MatDialogRef<CommandPalette> | null = null;
  private commandPaletteOpenedAt = 0;
  private rolePreviewRef: MatDialogRef<RolePreviewDialog> | null = null;
  private rolePreviewOpenedAt = 0;
  // Guards the close-on-second-press toggle below against firing too eagerly
  // right after opening (e.g. a bouncy key switch sending a double signal).
  private readonly TOGGLE_DEBOUNCE_MS = 300;

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.timeseriesPopoutOpen = false;
        this.managedDataPopoutOpen = false;
        this.customViewsPopoutOpen = false;
        this.drawerOpen.set(false);
      });
    // An unlock ends on its own; drop the banner once its time is up.
    interval(30_000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.monthLock.clearExpiredUnlock());
    // Someone can be added to an unlock (`~unlockadd`), or have it relocked
    // for them, and a month can lock, while their app is open: re-ask every
    // minute and when the tab comes back into view, so their pages follow
    // without a reload.
    merge(interval(60_000), fromEvent(document, 'visibilitychange'))
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.monthLock.refresh());
  }

  ngOnInit(): void {
    // Warms the command palette's i18n scopes as soon as the shell mounts —
    // Layout is created once per session, well before the user is likely to
    // press Ctrl/Cmd+K. Without this, the palette's own labels resolve fine
    // (its own 'ui/command-palette' scope loads via provideI18nScope on first
    // construction), but the first-ever open also has to fetch every foreign
    // namespace its page/group labels borrow from — until those resolve, the
    // whole palette (search input included) stays out of the DOM, which reads
    // as "slow to open" and, worse, means it isn't there yet for MatDialog's
    // one-time autoFocus pass to find. Prefetching here means that by the
    // time the palette actually opens, these scopes are normally already warm.
    void this.i18n.loadScope('ui/command-palette');
    COMMAND_PALETTE_NAMESPACES.forEach((namespace) => void this.i18n.loadScope(namespace));
  }

  protected toggleSidebar(): void {
    if (this.viewport.isCompact()) {
      this.drawerOpen.update((open) => !open);
      return;
    }
    this.sidebarCollapsed = !this.sidebarCollapsed;
    writeSidebarCollapsed(this.sidebarCollapsed);
    setTimeout(() => globalThis.dispatchEvent(new Event('resize')));
  }

  protected toggleTimeseriesPopout(): void {
    this.timeseriesPopoutOpen = !this.timeseriesPopoutOpen;
    if (this.timeseriesPopoutOpen) {
      this.managedDataPopoutOpen = false;
      this.customViewsPopoutOpen = false;
    }
    setTimeout(() => {
      this.showOverlay();
    });
  }

  protected toggleManagedDataPopout(): void {
    this.managedDataPopoutOpen = !this.managedDataPopoutOpen;
    if (this.managedDataPopoutOpen) {
      this.timeseriesPopoutOpen = false;
      this.customViewsPopoutOpen = false;
    }
    setTimeout(() => {
      this.showOverlay();
    });
  }

  protected toggleCustomViewsPopout(): void {
    this.customViewsPopoutOpen = !this.customViewsPopoutOpen;
    if (this.customViewsPopoutOpen) {
      this.timeseriesPopoutOpen = false;
      this.managedDataPopoutOpen = false;
    }
    setTimeout(() => {
      this.showOverlay();
    });
  }

  @HostListener('document:keydown', ['$event'])
  handleGlobalKeydown(event: KeyboardEvent): void {
    // Ignore OS key-repeat from holding the key down — without this, holding
    // Ctrl+K would rapid-fire open/close (or repeatedly no-op) instead of
    // acting like a single press.
    if (event.repeat) return;

    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.toggleCommandPalette();
      return;
    }

    // Ctrl+Alt+U: global-admins preview the whole UI as another role, so they
    // can create a role and immediately test what it can see. Gated on the REAL
    // admin status (not hasPermission, which honours the preview lens) so an
    // admin already previewing a lower role can still reopen the picker to
    // switch or exit. Uses event.code so an Alt-composed character on macOS
    // doesn't mask the 'u'. Unlike the `~` menu, this is not @anessa-only.
    if (
      event.ctrlKey &&
      event.altKey &&
      !event.metaKey &&
      event.code === 'KeyU' &&
      this.permissionService.isRealGlobalAdmin()
    ) {
      event.preventDefault();
      this.toggleRolePreview();
      return;
    }

    // Ctrl+Alt+W: close the active tab (see the ~tabs dev command). Plain
    // Ctrl+W/Cmd+W isn't usable here — Chrome and Firefox both treat it as a
    // protected shortcut for closing the real browser tab, so the keydown
    // event never reaches page JS and preventDefault() can't stop it.
    if (
      event.ctrlKey &&
      event.altKey &&
      !event.metaKey &&
      event.code === 'KeyW' &&
      this.tabViewService.enabled()
    ) {
      const activeTabId = this.tabViewService.activeTabId();
      if (activeTabId) {
        event.preventDefault();
        this.tabViewService.closeTab(activeTabId);
      }
      return;
    }

    // Ctrl+Alt+P: keep the page on screen as a tab, when it was reached by
    // ordinary navigation (sidebar, link) rather than through a tab.
    // Internal-only like the `~` menu, and only with tab view on; a no-op when
    // the page already is a tab.
    if (
      event.ctrlKey &&
      event.altKey &&
      !event.metaKey &&
      event.code === 'KeyP' &&
      this.tabViewService.enabled() &&
      isAnessaEmail(this.authService.ensuredUser()?.email)
    ) {
      event.preventDefault();
      this.tabViewService.tabifyCurrentPage((url) =>
        tabTitleFor(url, this.i18n, this.dashboardService),
      );
      return;
    }

    // Ctrl+Alt+N: toggle the notes scratchpad (the `~notes` dev command)
    // directly, for jotting things down while testing. Internal-only like the
    // `~` menu, so a silent no-op for non-@anessa users. Works over an open
    // dialog too, since the notes panel is a non-modal overlay.
    if (
      event.ctrlKey &&
      event.altKey &&
      !event.metaKey &&
      event.code === 'KeyN' &&
      isAnessaEmail(this.authService.ensuredUser()?.email)
    ) {
      event.preventDefault();
      this.devNotes.toggle();
      return;
    }

    // Secret shortcut straight into the `~` dev-command menu — hidden the
    // same way the menu itself is: only does anything for @anessa.com users,
    // so it's a silent no-op (not even opening the normal palette) for anyone else.
    if (
      (event.ctrlKey || event.metaKey) &&
      event.code === 'Backquote' &&
      isAnessaEmail(this.authService.ensuredUser()?.email)
    ) {
      event.preventDefault();
      this.toggleCommandPalette('~');
    }
  }

  /** Opens the palette, or closes it if a press comes in while one is already open. */
  toggleCommandPalette(initialSearchTerm?: string): void {
    if (this.commandPaletteRef) {
      if (Date.now() - this.commandPaletteOpenedAt < this.TOGGLE_DEBOUNCE_MS) {
        return;
      }
      this.commandPaletteRef.close();
      return;
    }

    this.commandPaletteOpenedAt = Date.now();
    this.commandPaletteRef = this.dialog.open(CommandPalette, {
      position: { top: '15vh' },
      width: '600px',
      panelClass: 'command-palette-panel',
      data: initialSearchTerm ? { initialSearchTerm } : undefined,
    });
    this.commandPaletteRef
      .afterClosed()
      .subscribe(() => (this.commandPaletteRef = null));
  }

  /** Opens the role-preview picker, or closes it if already open (Ctrl+Alt+U). */
  toggleRolePreview(): void {
    if (this.rolePreviewRef) {
      if (Date.now() - this.rolePreviewOpenedAt < this.TOGGLE_DEBOUNCE_MS) {
        return;
      }
      this.rolePreviewRef.close();
      return;
    }

    this.rolePreviewOpenedAt = Date.now();
    this.rolePreviewRef = this.dialog.open(RolePreviewDialog, {
      width: '520px',
      panelClass: 'role-preview-panel',
    });
    this.rolePreviewRef
      .afterClosed()
      .subscribe(() => (this.rolePreviewRef = null));
  }

  protected closeDrawer(): void {
    this.drawerOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  hidePopout() {
    this.drawerOpen.set(false);
    if (this.isBlurred) {
      this.managedDataPopoutOpen = false;
      this.timeseriesPopoutOpen = false;
      this.customViewsPopoutOpen = false;
    }
  }
  @HostListener('window:resize')
  showOverlay() {
    if (!this.isBlurred) return;

    const rect = this.mainRef().nativeElement.getBoundingClientRect();

    this.overlayStyle = {
      position: 'fixed',
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      zIndex: 9999,
      background: 'rgba(0,0,0,0.35)',
    };
  }
}

/** Where the desktop menu's collapsed state is kept (a per-browser convenience). */
export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'anessa.sidebarCollapsed';

function readSidebarCollapsed(): boolean {
  try {
    return globalThis.localStorage?.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeSidebarCollapsed(collapsed: boolean): void {
  try {
    globalThis.localStorage?.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    // Storage blocked (private mode, policy): the menu just opens expanded next time.
  }
}
